import React, { useState } from 'react';
import {
  Layout,
  Card,
  DataTable,
  DisplayText,
  Button,
  TextField
} from '@shopify/polaris';
import dayjs from 'dayjs';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import '../../modules/firebase';
import Container from 'components/Container';

const Customers = () => {
  const [customerId, setCustomerId] = useState();
  const [customerData, setCustomerData] = useState();
  const [status, setStatus] = useState();

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    try {
      if (ts.toMillis) return dayjs(ts.toMillis()).format('DD MMM YYYY');
      if (ts._seconds) return dayjs(ts._seconds * 1000).format('DD MMM YYYY');
      return dayjs(ts).format('DD MMM YYYY');
    } catch (e) {
      return '—';
    }
  };

  const getCustomerData = async customerId => {
    const userSnapshot = await getDoc(doc(getFirestore(), 'users', customerId));

    if (userSnapshot.exists()) {
      const codes = [];
      let runningBalance = 0;
      const userData = userSnapshot.data();
      for (const code of (userData.codes || [])) {
        const { id, status, type, updatedAt, usedAt, value, version } = code;
        const numericValue = typeof value === 'number' ? value : 0;
        runningBalance += numericValue;
        codes.push([
          id || '—',
          formatTimestamp(updatedAt),
          formatTimestamp(usedAt),
          value,
          status || '—',
          type || '—',
          version || '—',
          runningBalance
        ]);
      }

      const discounts = [];
      if (userData.discounts) {
        runningBalance = 0;
        for (const discount of userData.discounts) {
          const tokens = discount.tokens || 0;
          runningBalance += tokens;
          discounts.push([
            discount.code && discount.code.created_at ? dayjs(discount.code.created_at).format('DD MMM YYYY') : '—',
            discount.code ? discount.code.code : '—',
            discount.products && discount.products[0] ? discount.products[0].title : '—',
            discount.products && discount.products[0] ? discount.products[0].id : '—',
            tokens,
            runningBalance
          ]);
        }
      }
      setCustomerData({ codes, discounts });
      setStatus('');
    } else {
      setStatus(`Customer ID ${customerId} does not exist.`);
    }
  };

  const downloadCsvFile = (filename, text) => {
    const element = document.createElement('a');
    const file = new Blob([text], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
  };

  const headings = {
    codes: [
      'Code',
      'updatedAt',
      'usedAt',
      'value',
      'status',
      'type',
      'version',
      'Running balance'
    ],
    discounts: [
      'Created at',
      'discount code',
      'product title',
      'product ID',
      'tokens used',
      'Running balance'
    ]
  };

  return (
    <Container
      pageProps={{
        title: 'Customers',
        subtitle: 'Codes and discounts',
        fullWidth: true,
        primaryAction: (
          <div style={{ display: 'flex' }}>
            <TextField
              clearButton
              onClearButtonClick={() => {
                setCustomerData(null);
                setCustomerId(null);
                setStatus('');
              }}
              placeholder="Enter a customer ID..."
              onChange={input => setCustomerId(input)}
              value={customerId}
            />
            <Button onClick={() => getCustomerData(customerId)}>Search</Button>
          </div>
        )
      }}
    >
      <React.Fragment>
        <Layout.Section>
          {status && <DisplayText size="small">{status}</DisplayText>}
          {customerData && (
            <>
              <Card sectioned>
                <>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <DisplayText size="small">
                      Codes (total value{' '}
                      {customerData.codes.reduce(
                        (prev, curr) => prev + curr[3],
                        0
                      )}
                      )
                    </DisplayText>
                    <Button
                      onClick={() =>
                        downloadCsvFile(
                          `${customerId}-codes.csv`,
                          `${headings.codes.join(',')}\n${customerData.codes
                            .map(row => row.join(','))
                            .join('\n')}`
                        )
                      }
                    >
                      Export as CSV
                    </Button>
                  </div>

                  <DataTable
                    columnContentTypes={[
                      'text',
                      'text',
                      'text',
                      'numeric',
                      'text',
                      'text',
                      'numeric',
                      'numeric'
                    ]}
                    headings={headings.codes}
                    rows={customerData.codes}
                  />
                </>
              </Card>
              <Card sectioned>
                <>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <DisplayText size="small">
                      Discounts (total{' '}
                      {customerData.discounts.reduce(
                        (prev, curr) => prev + curr[4],
                        0
                      )}
                      )
                    </DisplayText>
                    <Button
                      onClick={() =>
                        downloadCsvFile(
                          `${customerId}-discounts.csv`,
                          `${headings.discounts.join(
                            ','
                          )}\n${customerData.discounts
                            .map(row => row.join(','))
                            .join('\n')}`
                        )
                      }
                    >
                      Export as CSV
                    </Button>
                  </div>

                  <DataTable
                    columnContentTypes={[
                      'text',
                      'text',
                      'text',
                      'numeric',
                      'numeric',
                      'numeric'
                    ]}
                    headings={headings.discounts}
                    rows={customerData.discounts}
                  />
                </>
              </Card>
            </>
          )}
        </Layout.Section>
      </React.Fragment>
    </Container>
  );
};

export default Customers;
